import React, { useState, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import {
    Box,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    MenuItem,
    FormControl,
    Select,
    TextareaAutosize,
    CircularProgress,
} from '@mui/material';
import { FieldProps, FormikProps } from 'formik';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { COLORS } from '../../../theme/themeConstants';
import { useAtomValue } from 'jotai';
import { offerAtom } from './ManageOffers';
import { useCreateOffer, useEditOffer, useGenerateCouponCode } from '../../Query/OffersQuery';
import { AddOfferRequestSchemaCouponTypeEnum } from '../../../client-sdk/api';
import { useQueryClient } from '@tanstack/react-query';

interface EditOffersProps {
    onCancel?: () => void;
}

type OfferType = 'ProductCoupon' | 'StoreCoupon' | 'NewUsers';

const resolveOfferType = (offerType?: string): OfferType => {
    if (offerType === 'StoreCoupon') return 'StoreCoupon';
    if (offerType === 'NewUsers') return 'NewUsers';
    return 'ProductCoupon';
};

const EditOffers = ({ onCancel }: EditOffersProps) => {
     const { warning } = useThemeColors();
    const offerData = useAtomValue(offerAtom);
    const isEditMode = !!offerData?.offerId;
    const [autoGenerate, setAutoGenerate] = useState(!isEditMode);
    const [couponCode, setCouponCode] = useState(offerData?.couponCode || '');
    const resetFormRef = React.useRef<(() => void) | null>(null);
    const formRef = React.useRef<any>(null);
    const queryClient = useQueryClient();
    
    const { mutate: createOffer, isPending: createLoading } = useCreateOffer();
    const { mutate: editOffer, isPending: editLoading } = useEditOffer();
    const { mutate: generateCoupon, isPending: generateLoading } = useGenerateCouponCode();
    
    // Get all offers to check for duplicates
    const getAllOffersForValidation = () => {
        const offersData = queryClient.getQueriesData({ queryKey: ['getOffers'] });
        const allOffers: any[] = [];
        offersData.forEach(([, data]: any) => {
            if (data?.data) {
                allOffers.push(...(Array.isArray(data.data) ? data.data : []));
            }
        });
        return allOffers;
    };

    // Load offer data when editing
    useEffect(() => {
        if (isEditMode && offerData) {
            setCouponCode(offerData.couponCode || '');
        }
    }, [isEditMode, offerData]);

    // Auto-generate coupon code on initial load for new offers
    useEffect(() => {
        if (!isEditMode && autoGenerate && !couponCode) {
            generateCoupon(undefined, {
                onSuccess: (data: any) => {
                    if (data?.couponCode) {
                        setCouponCode(data.couponCode);
                        // Update form field if form is already initialized
                        if (formRef.current) {
                            formRef.current.setFieldValue('couponCode', data.couponCode);
                            formRef.current.setFieldTouched('couponCode', true, false);
                            formRef.current.setFieldError('couponCode', undefined);
                        }
                    }
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync couponCode state changes to form
    useEffect(() => {
        if (couponCode && formRef.current && autoGenerate) {
            formRef.current.setFieldValue('couponCode', couponCode, false);
            formRef.current.setFieldTouched('couponCode', true, false);
            formRef.current.setFieldError('couponCode', undefined);
        }
    }, [couponCode, autoGenerate]);

    // Memoize initialValues to prevent form reset when couponCode changes
    // Only recalculate when offerData changes (not when couponCode state changes)
    const initialValues = useMemo(() => ({
        couponCode: isEditMode ? (offerData?.couponCode || '') : '',
        couponName: offerData?.couponName || '',
        type: offerData?.couponType === 'Percentage' ? 'percentage' : offerData?.couponType === 'Flat' ? 'flat' : '',
        value: offerData?.couponValue != null ? offerData.couponValue : '',
        validityDays: offerData?.validityDays != null ? offerData.validityDays : '',
        startDate: offerData?.couponStartDate ? offerData.couponStartDate.split('T')[0] : '',
        terms: offerData?.termsAndConditions || '',
        couponPerUser: offerData?.couponPerUserLimit != null ? offerData.couponPerUserLimit : '',
        quantity: offerData?.totalCouponCount != null ? offerData.totalCouponCount : '',
        maxCouponAmount: offerData?.maxCouponAmount != null ? Number(offerData.maxCouponAmount) : undefined,
    }), [isEditMode, offerData]);

    const validationSchema = Yup.object().shape({
        couponCode: Yup.string()
            .required('Coupon Code is required')
            .test('unique-coupon-code', 'Coupon Code already exists', function(value) {
                if (!value) return true;
                const allOffers = getAllOffersForValidation();
                const existingOffer = allOffers.find(
                    (offer: any) => 
                        offer.couponCode?.toLowerCase() === value.toLowerCase() &&
                        (!isEditMode || offer.offerId !== offerData?.offerId)
                );
                return !existingOffer;
            }),
        couponName: Yup.string()
            .required('Coupon Name is required')
            .test('unique-coupon-name', 'Coupon Name already exists', function(value) {
                if (!value) return true;
                const allOffers = getAllOffersForValidation();
                const existingOffer = allOffers.find(
                    (offer: any) => 
                        offer.couponName?.toLowerCase() === value.toLowerCase() &&
                        (!isEditMode || offer.offerId !== offerData?.offerId)
                );
                return !existingOffer;
            }),
        type: Yup.string().required('Type is required'),
        value: Yup.number().typeError('Value must be a number').required('Value is required'),
        validityDays: Yup.number().typeError('Validity Days must be a number').required('Validity Days is required'),
        startDate: Yup.string().required('Start Date is required'),
        terms: Yup.string().required('Terms & Conditions are required'),
        couponPerUser: Yup.number().typeError('Coupon per User must be a number').required('Coupon per User is required'),
        quantity: Yup.number().typeError('Quantity must be a number').required('Quantity is required'),
    });

    return (
        <>
            {AllForm({
                initialValues: initialValues,
                validationSchema: validationSchema,
                enableReinitialize: true,
                validateOnChange: true,
                validateOnBlur: true,
                onSubmit: async (values: Record<string, any>) => {
                    try {
                        // Map form values to API schema
                        const couponType = values.type === 'percentage' 
                            ? AddOfferRequestSchemaCouponTypeEnum.Percentage 
                            : AddOfferRequestSchemaCouponTypeEnum.Flat;
                        
                        // Default to ProductCoupon since Type of Coupon field is removed
                        const offerType = resolveOfferType(offerData?.offerType);

                        // Convert date string to ISO 8601 date-time format
                        // Date input gives "YYYY-MM-DD", we need "YYYY-MM-DDTHH:mm:ss.sssZ"
                        const formatDateToISO = (dateString: string): string => {
                            if (!dateString) return '';
                            // If already in ISO format, return as is
                            if (dateString.includes('T')) return dateString;
                            // Convert "YYYY-MM-DD" to "YYYY-MM-DDTHH:mm:ss.sssZ"
                            const date = new Date(dateString);
                            return date.toISOString();
                        };

                        const formattedStartDate = formatDateToISO(values.startDate);

                        if (isEditMode && offerData?.offerId) {
                            // Edit mode
                            const editPayload: any = {
                                couponName: values.couponName,
                                couponType: couponType,
                                couponValue: Number(values.value),
                                validityDays: Number(values.validityDays),
                                couponStartDate: formattedStartDate,
                                termsAndConditions: values.terms,
                                offerType: offerType,
                                couponPerUserLimit: Number(values.couponPerUser),
                                totalCouponCount: Number(values.quantity),
                            };
                            
                            if (values.maxCouponAmount) {
                                editPayload.maxCouponAmount = Number(values.maxCouponAmount);
                            }

                            editOffer(
                                { offerId: offerData.offerId, data: editPayload },
                                {
                                    onSuccess: () => {
                                        if (resetFormRef.current) {
                                            resetFormRef.current();
                                        }
                                        setCouponCode('');
                                        // Invalidate and refetch offers list
                                        queryClient.invalidateQueries({
                                            queryKey: ['getOffers'],
                                            exact: false,
                                        });
                                        // Close modal after successful update
                                        // Use setTimeout to ensure state updates are processed
                                        setTimeout(() => {
                                            if (onCancel) {
                                                onCancel();
                                            }
                                        }, 100);
                                    },
                                }
                            );
                        } else {
                            // Create mode - use couponCode from form values (which should match state)
                            const finalCouponCode = values.couponCode || couponCode;
                            if (!finalCouponCode) {
                                // Note: We can't set field error here as formikHelpers is not available
                                // The validation should catch this via the schema
                                return;
                            }
                            
                            const createPayload = {
                                couponCode: finalCouponCode,
                                couponName: values.couponName,
                                couponType: couponType,
                                couponValue: Number(values.value),
                                validityDays: Number(values.validityDays),
                                couponStartDate: formattedStartDate,
                                termsAndConditions: values.terms,
                                offerType: offerType,
                                couponPerUserLimit: Number(values.couponPerUser),
                                totalCouponCount: Number(values.quantity),
                                ...(values.maxCouponAmount ? { maxCouponAmount: Number(values.maxCouponAmount) } : {}),
                            };

                            createOffer(createPayload, {
                                onSuccess: () => {
                                    if (resetFormRef.current) {
                                        resetFormRef.current();
                                    }
                                    setCouponCode('');
                                    // Invalidate and refetch offers list
                                    queryClient.invalidateQueries({
                                        queryKey: ['getOffers'],
                                        exact: false,
                                    });
                                    // Close modal after successful creation
                                    // Use setTimeout to ensure state updates are processed
                                    setTimeout(() => {
                                        if (onCancel) {
                                            onCancel();
                                        }
                                    }, 100);
                                },
                            });
                        }
                    } catch (error) {
                        console.error('Form submission error:', error);
                    }
                },
                fields: [
                    {
                        name: 'codeGeneration',
                        // label: 'Type',
                        isRequired: false,
                        type: 'customWithError',
                        component: ({ form }: any) => (
                            <RadioGroup
                                row
                                value={autoGenerate ? 'auto' : 'custom'}
                                onChange={e => {
                                    setAutoGenerate(e.target.value === 'auto');
                                    if (e.target.value === 'auto') {
                                        // Generate coupon code via API
                                        generateCoupon(undefined, {
                                            onSuccess: (data: any) => {
                                                if (data?.couponCode) {
                                                    setCouponCode(data.couponCode);
                                                    form.setFieldValue('couponCode', data.couponCode);
                                                }
                                            },
                                        });
                                    } else {
                                        setCouponCode('');
                                        form.setFieldValue('couponCode', '');
                                    }
                                }}
                            >
                                <FormControlLabel value="auto" control={<Radio />} label="Auto Generate Coupon Code" />
                                <FormControlLabel value="custom" control={<Radio />} label="Custom Coupon Code" />
                            </RadioGroup>
                        ),
                    },
                    {
                        name: 'couponCode',
                        label: 'Coupon Code',
                        isRequired: true,
                        type: 'customWithError',
                        component: ({ field, form }: any) => {
                            // Store form instance in ref for external updates
                            if (!formRef.current) {
                                formRef.current = form;
                            }
                            
                            // Use couponCode state as the source of truth when auto-generating
                            const currentValue = autoGenerate ? (couponCode || field.value || '') : (field.value || '');
                            
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <TextField
                                        {...field}
                                        value={currentValue}
                                        onChange={e => {
                                            const newValue = e.target.value;
                                            setCouponCode(newValue);
                                            form.setFieldValue('couponCode', newValue);
                                            form.setFieldTouched('couponCode', true, false);
                                        }}
                                        onBlur={field.onBlur}
                                        disabled={autoGenerate}
                                        error={form.touched.couponCode && Boolean(form.errors.couponCode)}
                                        sx={{ width: 200 }}
                                        size="small"
                                    />
                                    {autoGenerate && (
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            onClick={() => {
                                                generateCoupon(undefined, {
                                                    onSuccess: (data: any) => {
                                                        if (data?.couponCode) {
                                                            setCouponCode(data.couponCode);
                                                            form.setFieldValue('couponCode', data.couponCode);
                                                            form.setFieldTouched('couponCode', true, false);
                                                            // Clear any validation errors
                                                            form.setFieldError('couponCode', undefined);
                                                        }
                                                    },
                                                });
                                            }}
                                            disabled={generateLoading}
                                        >
                                            {generateLoading ? 'Generating...' : 'Generate'}
                                        </Button>
                                    )}
                                </Box>
                            );
                        },
                    },
                    { name: 'couponName', label: 'Coupon Name', isRequired: true, type: 'text' },
                    {
                        name: 'type',
                        label: 'Type',
                        isRequired: true,
                        type: 'select',
                        selectLength: true,
                        selectWidth: 400,
                        component: ({ field }: any) => (
                            <FormControl size="medium">
                                <Select {...field} displayEmpty sx={{ width: 400 }}>
                                    <MenuItem value=""><em>Select</em></MenuItem>
                                    <MenuItem value="percentage">Percentage</MenuItem>
                                    <MenuItem value="flat">Flat</MenuItem>
                                </Select>
                            </FormControl>
                        ),
                    },
                    { name: 'value', label: 'Value', isRequired: true, type: 'number' },
                    { name: 'maxCouponAmount', label: 'Max Coupon Amount (Optional)', isRequired: false, type: 'number' },
                    { name: 'validityDays', label: 'Validity Days', isRequired: true, type: 'number' },
                    {
                        name: 'startDate',
                        label: 'Start Date',
                        isRequired: true,
                        type: 'customWithError',
                        component: ({ field }: any) => (
                            <TextField
                                {...field}
                                type="date"
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: new Date().toISOString().split('T')[0],
                                    style: { cursor: 'pointer' }
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        width: '400px !important', // 💥 Force width on the input wrapper
                                    },
                                }}
                            />
                        ),
                    },
                    {
                        name: 'terms',
                        label: 'Terms & Conditions',
                        isRequired: true,
                        type: 'customWithError',
                        component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                            const error = form.touched.terms && Boolean(form.errors.terms);
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: 400 }}>
                                    <TextareaAutosize
                                        id={field.name}
                                        name={field.name}
                                        minRows={4}
                                        maxRows={10}
                                        placeholder="Enter terms and conditions..."
                                        value={field.value || ''}
                                        style={{
                                            padding: 8,
                                            fontSize: 14,
                                            borderRadius: 4,
                                            border: `1px solid ${error ? '#d32f2f' : '#ccc'}`,
                                            outline: 'none',
                                            resize: 'vertical',
                                        }}
                                        onBlur={form.handleBlur}
                                        onChange={form.handleChange}
                                    />
                                </Box>
                            );
                        },
                    },
                   
                    { name: 'couponPerUser', label: 'Coupon per User', isRequired: true, type: 'number' },
                    { name: 'quantity', label: 'Quantity', isRequired: true, type: 'number' },
                ],
                // customButton: formikProps => (
                //     <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                //         <Button type="submit" variant="contained" color="warning">Add</Button>
                //         <Button type="button" variant="outlined" color="error" onClick={onCancel}>Cancel</Button>
                //     </Box>
                // ),
                customButton: ({ handleSubmit, validateForm, setTouched, isSubmitting, resetForm }: any) => {
                    // Store resetForm reference so we can use it in success callbacks
                    resetFormRef.current = resetForm;
                    
                    const buildTouchedState = (validationErrors: Record<string, any>) => {
                        const touchedState: Record<string, boolean> = {};
                        Object.keys(validationErrors).forEach((key) => {
                            touchedState[key] = true;
                        });
                        return touchedState;
                    };

                    return (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                type="button"
                                variant="contained"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 14,
                                    px: 3,
                                    py: 1,
                                    backgroundColor: COLORS.primary.main,
                                    '&:hover': {
                                        backgroundColor: COLORS.primary.dark,
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                        color: 'rgba(0, 0, 0, 0.26)',
                                    },
                                }}
                                onClick={async () => {
                                    const validationErrors = await validateForm();
                                    if (Object.keys(validationErrors).length > 0) {
                                        if (setTouched) {
                                            setTouched(buildTouchedState(validationErrors), false);
                                        }
                                        return;
                                    }
                                    handleSubmit();
                                }}
                                disabled={createLoading || editLoading || isSubmitting}
                            >
                                {createLoading || editLoading || isSubmitting ? (
                                    <>
                                        <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : isEditMode ? (
                                    'Update'
                                ) : (
                                    'Create'
                                )}
                            </Button>

                                <Button
                                    type="button"
                                    variant="outlined"
                                onClick={() => {
                                    if (resetFormRef.current) {
                                        resetFormRef.current();
                                    }
                                    setCouponCode('');
                                    setAutoGenerate(!isEditMode);
                                }}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: 14,
                                        px: 3,
                                        py: 1,
                                        borderColor: COLORS.secondary.main,
                                        color: COLORS.secondary.main,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                            color: 'rgba(0, 0, 0, 0.26)',
                                        },
                                    }}
                                >
                                    Reset
                                </Button>
                            <Button
                                type="button"
                                variant="outlined"
                                color="error"
                                onClick={onCancel}
                                sx={{
                                    bgcolor: warning.main,
                                    color: '#fff',
                                    '&:hover': {
                                        bgcolor: warning.dark,
                                        color: '#fff',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    );
                },
            })()}
        </>
    );
};

export default EditOffers;
